/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useEffect, useState } from 'react';

import {
	ActionIcon,
	Badge,
	Box,
	Paper,
} from '@mantine/core';

import { useDisclosure } from '@mantine/hooks';

// Styles
import classes from './offsets.module.css';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import {
	IconFilter,
	IconPlus,
} from '@tabler/icons-react';

// Apis

// Store
import useStore from '@store/index';

// Datattable config
import { tableSchemaBase as columns } from '@features/offsets/offsets.columns';
import { tableSchemaBase as columnsWithoutBrand } from '@features/offsets/offsetsWithoutBrand.columns';
import { tableSchemaBase as columnsWithoutMonetization } from '@features/offsets/offsetsWithoutMonetization.columns';

// Utils
import { truncateString } from '@utils/functions';

// Hooks
import { useOffsetList } from '@features/offsets/hooks/useOffsetList';

// Constants
import { ROLES } from '@config/constants/roles';

// Authorizations
import { useAuthorization } from '@lib/authorization';

// Local components

// Types
import { useSendCertificate } from '@features/offsets/api/send-certificate';
import { TOffsetPopulated } from '@features/offsets/types';
import {
	TCertificate,
	TTargetColumn,
} from '@global-types/index';
import { queryClient } from '@lib/react-query';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import findIndex from 'lodash/findIndex';

import { Button } from '@components/v2/ui';
import { Datatable } from '@components/v2/ui/Datatable/Datatable';
import { ModalSendCertificate } from '@components/v2/ui/ModalSendCertificate';
import { Pagination } from '@components/v2/ui/Pagination';
import { ACLS } from '@config/constants/acls';
import { useCreateCertificate } from '@features/offsets/api/create-certificate';
import { getCertificate } from '@features/offsets/api/get-certificate';
import { useOffsets } from '@features/offsets/api/get-offsets';
import { sendCertificate } from '@features/offsets/api/send-certificate-asynchrenous';
import CreateOffset from '@features/offsets/components/CreateOffset/CreateOffset';
import { DetailsOffset } from '@features/offsets/components/DetailsOffset';
import { Filter } from '@features/offsets/components/Filter';
import { checkAclFront } from '@utils/acl-front';
import { isEmpty, isNil } from 'lodash';
import get from 'lodash/get';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Offsets = () => {
	const { t, i18n } = useTranslation();

	const [opened, { open, close }] = useDisclosure(false);
	const [
		openedFilters,
		{ open: openFilters, close: closeFilters },
	] = useDisclosure(false);

	const [
		offsetDetailsOpened,
		{
			open: openOffsetDetails,
			close: closeOffsetDetails,
		},
	] = useDisclosure(false);

	const {
		offsetsPage,
		offsetsPageSize,
		offsetsQueryParams,
		offsetsSortParams,
		offsetsSortStatus,
		setOffsetsSelectedRow,
		offsetsSelectedRow,
		setOffsetsPage,
		setOffsetsPageSize,
		setOffsetsQueryParams,
		setOffsetsSortParams,
		setOffsetsSortStatus,
		setOffsetsDatatableOptions,
		offsetsDatatableOptions,
		getUser,
		offsetsDatatableWithoutBrandOptions,
		setOffsetsDatatableWithoutBrandOptions,
		offsetsDatatableWithoutMonetizationOptions,
		setOffsetsDatatableWithoutMonetizationOptions,
		setIsAddOffset,
		isAddOffset,
		setCurrentStepOffsetCreate,
		setOffsetMesurabilityProjectsSelected,
		setOffsetMesurabilityProjectsQueryParams,
		setOffsetCreationActiveStep,
		setSuccessModalOpened,
		setOffsetAdditionalProjectsSelected,
	} = useStore();

	const user = getUser();

	const {
		data,
		isLoading,
		total: totalItems,
		totalPages,
	} = useOffsets(
		offsetsPage,
		offsetsPageSize,
		offsetsQueryParams,
		offsetsSortParams
	);

	const {
		mutate,
		isLoading: isLoadingCreateCertificate,
	} = useCreateCertificate();

	const {
		mutate: mutateSendCertificate,
		isLoading: isLoadingSendCertificate,
	} = useSendCertificate();

	const [
		,
		,
		,
		handleFilterBrands,
		,
		,
		,
		handleChangePage,
		handleChangeSortStatus,
		handleChangePageSize,
	] = useOffsetList();

	const handleReset = () => {
		setOffsetsPageSize(100);
		setOffsetsPage(1);
		setOffsetsQueryParams({});
		setOffsetsSortParams('-createdAt _id');
		setOffsetsSortStatus({});
		setIsAddOffset(false);
		setCurrentStepOffsetCreate(0);
		setOffsetCreationActiveStep(0);
		setOffsetMesurabilityProjectsSelected([]);
		setOffsetAdditionalProjectsSelected([]);
		setOffsetMesurabilityProjectsQueryParams({});
		setSuccessModalOpened(false);
	};

	useEffect(() => {
		handleReset();
		return () => {
			handleReset();
		};
	}, []);

	const getVisibleColumns = (
		columnsConfig: any[],
		columnOrderConfig: any[]
	) => {
		const activeColumns = columnOrderConfig
			.filter((config) => config.checked)
			.map((config) => config.name);

		const visibleColumns = columnsConfig.filter((col) =>
			activeColumns.includes(col.accessor)
		);

		return columnOrderConfig
			.filter((config) => config.checked)
			.map((config) => {
				const column = visibleColumns.find(
					(col) => col.accessor === config.name
				);
				return column || {};
			});
	};

	const handleCloseModalSendCertificate = () => {
		close();
		setOffsetsSelectedRow({});
	};

	const handleDownloadCertificate = async (data: any) => {
		const { certificates } = data;
		const foundCertificate = certificates
			? certificates.find(
					(item: any) => item.language == i18n.language
				)
			: null;

		await handleDownloadCertificateApi(
			data.id,
			data.name,
			data.entity.name,
			data.contributionDate,
			foundCertificate?.id || '',
			i18n.language,
			data.certificates
		);
	};
	const handleDownloadCertificateApi = async (
		id: string,
		nameOffset: string,
		nameBrand: string,
		contributionDate: string,
		certificateId: string,
		lang: string,
		certificates: TCertificate[]
	) => {
		const idNotification = notifications.show({
			loading: true,
			title: `${t(
				`offsets.notifications.loadingTheCertificate`
			)} ${truncateString(nameOffset, 18)}`,
			message: `${t(
				`offsets.notifications.theDownloadOfCertification`
			)} ${nameOffset} ${t(
				`offsets.notifications.isCurrentlyInProgress`
			)}`,
			autoClose: false,
			withCloseButton: false,
		});

		const parsedContributionDate = dayjs(
			contributionDate,
			'DD/MM/YYYY'
		).format('DD-MM-YYYY');

		let offsetHasCertificate: boolean = false;

		if (certificates && Array.isArray(certificates)) {
			offsetHasCertificate =
				findIndex(certificates, {
					language: lang,
				}) !== -1;
		}

		if (offsetHasCertificate) {
			await getCertificate(
				certificateId,
				nameOffset,
				nameBrand,
				parsedContributionDate,
				idNotification
			);
		} else {
			mutate(
				{
					offset: id,
					language: lang,
				},
				{
					onSuccess: async (response) => {
						await getCertificate(
							response.id,
							nameOffset,
							nameBrand,
							parsedContributionDate,
							idNotification
						);

						queryClient.invalidateQueries({
							queryKey: [
								'offsets',
								{
									page: offsetsPage,
									size: offsetsPageSize,
									queryParams: offsetsQueryParams,
									sortParams: offsetsSortParams,
								},
							],
						});
					},
				}
			);
		}
	};

	const handleSendCertificate = async (data: {
		other?: string;
		recepient: string | null;
		language: string;
	}) => {
		let offsetHasCertificate: boolean = false;

		if (
			offsetsSelectedRow.certificates &&
			Array.isArray(offsetsSelectedRow.certificates)
		) {
			offsetHasCertificate =
				findIndex(offsetsSelectedRow.certificates, {
					language: data.language,
				}) !== -1;
		}

		if (offsetHasCertificate) {
			const certificateFound =
				offsetsSelectedRow.certificates.find(
					(item: TCertificate) =>
						item.language == data.language
				);
			mutateSendCertificate(
				{
					certificateId: certificateFound.id,
					...(data &&
						!isNil(data.recepient) &&
						data.recepient !== 'other' && {
							email: data.recepient,
						}),

					...(data &&
						!isNil(data.recepient) &&
						data.recepient == 'other' &&
						!isNil(data.other) && { email: data.other }),
				},
				{
					onSuccess: async () => {
						close();
					},
				}
			);
		} else {
			mutate(
				{
					offset: offsetsSelectedRow.id,
					language: data.language,
				},
				{
					onSuccess: async (response) => {
						const idNotification = notifications.show({
							loading: true,
							title: t('global.In progress'),
							message: t(
								`notifications.certificateInProgress`
							),
							color: 'var(--mantine-color-igluGreen-9)',
							autoClose: false,
							withCloseButton: false,
						});
						const certificateId = response.id;

						await sendCertificate({
							idNotification,
							certificateId: certificateId,
							...(data &&
								!isNil(data.recepient) &&
								data.recepient !== 'other' && {
									email: data.recepient,
								}),

							...(data &&
								!isNil(data.recepient) &&
								data.recepient == 'other' &&
								!isNil(data.other) && {
									email: data.other,
								}),
						});
						handleCloseModalSendCertificate();
						queryClient.invalidateQueries({
							queryKey: [
								'offsets',
								{
									page: offsetsPage,
									size: offsetsPageSize,
									queryParams: offsetsQueryParams,
									sortParams: offsetsSortParams,
								},
							],
						});
					},
				}
			);
		}
	};

	const getRecipientOptions = (
		offsetsSelectedRow: TOffsetPopulated,
		user: any
	) => {
		const options = [];

		const IS_ADMIN =
			user.role == ROLES.ADMIN ||
			user.role == ROLES.SUPERADMIN ||
			user.role == ROLES.STAFF;

		const ownerEmail = get(
			offsetsSelectedRow,
			'organizationData.owner.email'
		);

		if (ownerEmail && IS_ADMIN) {
			options.push({
				label:
					`(${t('global.organizationOwner')}) ` +
					ownerEmail,
				value: ownerEmail,
			});
		}

		if (typeof user !== 'boolean' && user?.email) {
			options.push({
				label: `(${t('global.you')}) ` + user.email,
				value: user.email,
			});
		}

		const contactEmail = get(
			offsetsSelectedRow,
			'entity.contact.email'
		);
		if (contactEmail && IS_ADMIN) {
			options.push({
				label:
					`(${t('global.brandContact')}) ` + contactEmail,
				value: contactEmail,
			});
		}
		const uniqueOptions = options.filter(
			(item, index, self) =>
				self.findIndex(
					(el) => el.value === item.value
				) === index
		);

		return uniqueOptions;
	};

	const [filtersNbr, setFiltersNbr] = useState(0);

	const { checkRole, checkAcls, role } =
		useAuthorization();

	return (
		<div>
			{!isAddOffset && (
				<Paper className={classes['main-layout']}>
					{checkAclFront(
						ACLS.FRONT.OFFSETS.FILTER,
						role as ROLES
					) && (
						<Filter
							opened={openedFilters}
							onClose={closeFilters}
							title={t('filters.titles.offsets')}
							onApplyFilters={(filters) => {
								setFiltersNbr(
									Object.keys(filters).length
								);
							}}
							value={offsetsQueryParams}
						/>
					)}

					<DetailsOffset
						opened={
							!isEmpty(offsetsSelectedRow) &&
							offsetDetailsOpened
						}
						data={offsetsSelectedRow}
						onClose={() => {
							closeOffsetDetails();
							setOffsetsSelectedRow({});
						}}
					/>
					<ModalSendCertificate
						loading={
							isLoadingSendCertificate ||
							isLoadingCreateCertificate
						}
						opened={opened}
						recepientOptions={getRecipientOptions(
							offsetsSelectedRow,
							user
						)}
						handlesubmit={(data: any) => {
							handleSendCertificate(data);
						}}
						handleclose={handleCloseModalSendCertificate}
					/>
					<Box className={classes['action-bar']}>
						<div style={{ position: 'relative' }}>
							{filtersNbr > 0 && (
								<Badge
									style={{
										position: 'absolute',
										zIndex: '3',
										right: '-3px',
										fontFamily: 'InterDisplay-Regular',
									}}
									color="var(--mantine-color-igluRed-9)"
									size="xs"
									circle
								>
									{filtersNbr}
								</Badge>
							)}

							<ActionIcon
								variant="outline"
								size="48px"
								radius="xl"
								color="var(--mantine-color-igluBorder-9)"
								data-testid="bell-icon-button"
								onClick={openFilters}
								bg={
									!isEmpty(offsetsQueryParams)
										? 'var(--mantine-color-igluPrimary-9)'
										: 'transparent'
								}
							>
								<IconFilter
									color={
										!isEmpty(offsetsQueryParams)
											? 'var(--mantine-color-igluTextButton-9)'
											: 'var(--mantine-color-igluPrimary-9)'
									}
									radius="xl"
									size={24}
									stroke={1.5}
								/>
							</ActionIcon>
						</div>

						{checkAcls([ACLS.OFFSETS.CREATE]) &&
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
							]) && (
								<Button
									leftSection={
										<IconPlus
											radius="xl"
											size={24}
											stroke={1.5}
										/>
									}
									onClick={() => {
										setIsAddOffset(true);
									}}
									variant={'primary'}
								>
									{t('offsets.addOffset')}
								</Button>
							)}
					</Box>
					<Box className={classes['container-table']}>
						{Array.isArray(data) && (
							<Datatable
								fetching={isLoading}
								onRowClick={(data: any) => {
									setOffsetsSelectedRow(data.record);
									openOffsetDetails();
								}}
								handleUpdateColumns={(
									cols: TTargetColumn[]
								) => {
									checkRole([
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
									])
										? setOffsetsDatatableOptions(
												getVisibleColumns(columns, cols)
											)
										: checkRole([ROLES.BRAND_ADMIN])
											? setOffsetsDatatableWithoutBrandOptions(
													getVisibleColumns(
														columnsWithoutBrand,
														cols
													)
												)
											: checkRole([ROLES.STAFF])
												? setOffsetsDatatableWithoutMonetizationOptions(
														getVisibleColumns(
															columnsWithoutMonetization,
															cols
														)
													)
												: setOffsetsDatatableOptions(
														getVisibleColumns(
															columns,
															cols
														)
													);
								}}
								allColumns={
									checkRole([
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
									])
										? columns
										: checkRole([ROLES.BRAND_ADMIN])
											? columnsWithoutBrand
											: checkRole([ROLES.STAFF])
												? columnsWithoutMonetization
												: columns
								}
								columns={
									checkRole([
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
									])
										? offsetsDatatableOptions
										: checkRole([ROLES.BRAND_ADMIN])
											? offsetsDatatableWithoutBrandOptions
											: checkRole([ROLES.STAFF])
												? offsetsDatatableWithoutMonetizationOptions
												: offsetsDatatableOptions
								}
								records={data}
								onSortStatusChange={
									handleChangeSortStatus
								}
								sortstatus={offsetsSortStatus}
								onDownload={handleDownloadCertificate}
								onSend={(offset: any) => {
									setOffsetsSelectedRow(offset);
									open();
								}}
							/>
						)}
					</Box>
					<Box className={classes['pagination-bar']}>
						<Pagination
							pageSize={offsetsPageSize}
							totalItems={totalItems}
							activePage={offsetsPage}
							onPageSizeChange={handleChangePageSize}
							onChange={handleChangePage}
							total={totalPages ? totalPages : 0}
						/>
					</Box>
				</Paper>
			)}
			{isAddOffset && <CreateOffset />}
		</div>
	);
};

export default Offsets;
